/*
 * rt-cart-variant-switcher.js
 * In-cart variant switcher: lets shoppers change Color/Size on a line item
 * without leaving the cart drawer. Resolves variant ID client-side from a
 * JSON dump, calls /cart/change.js (or /cart/update.js for merges), then
 * fires theme:cart:refresh so the Broadcast cart drawer re-renders.
 * Lines are addressed by key (data-line-key), never by index. Messages come from
 * data-msg-* on the root (locale rt.cart.*); a missing one is not shown.
 */
(function () {
  'use strict';

  var SELECTOR_ROOT   = '[data-rt-cart-variant-switcher]';
  var SELECTOR_SELECT = '[data-rt-variant-option]';
  var SELECTOR_JSON   = '[data-rt-variants-json]';
  var INIT_FLAG       = 'rtInited';

  function parseVariants(root) {
    var script = root.querySelector(SELECTOR_JSON);
    if (!script) return [];
    try {
      return (JSON.parse(script.textContent) || {}).variants || [];
    } catch (e) {
      return [];
    }
  }

  function getSelects(root) {
    return Array.prototype.slice.call(root.querySelectorAll(SELECTOR_SELECT))
      .sort(function (a, b) {
        return parseInt(a.dataset.optionPosition, 10) - parseInt(b.dataset.optionPosition, 10);
      });
  }

  function getSelectedValues(root) {
    return getSelects(root).map(function (s) { return s.value; });
  }

  function findVariant(variants, values) {
    return variants.find(function (v) {
      if (!Array.isArray(v.options) || v.options.length !== values.length) return false;
      for (var i = 0; i < values.length; i++) {
        if (v.options[i] !== values[i]) return false;
      }
      return true;
    });
  }

  function isAvailable(variant) {
    if (!variant) return false;
    if (variant.available === false) return false;
    if (variant.inventory_management && variant.inventory_policy === 'deny' && (variant.inventory_quantity || 0) <= 0) {
      return false;
    }
    return true;
  }

  function updateDisabledOptions(root, variants) {
    var selects = getSelects(root);
    var current = selects.map(function (s) { return s.value; });
    selects.forEach(function (select, idx) {
      Array.prototype.slice.call(select.options).forEach(function (opt) {
        var candidate = current.slice();
        candidate[idx] = opt.value;
        var variant = findVariant(variants, candidate);
        opt.disabled = !isAvailable(variant);
      });
    });
  }

  function revertToCurrentVariant(root, variants) {
    var currentId = parseInt(root.dataset.currentVariantId, 10);
    var current = variants.find(function (v) { return v.id === currentId; });
    if (!current) return;
    getSelects(root).forEach(function (s, i) { s.value = current.options[i]; });
  }

  function setLoading(root, loading) {
    root.classList.toggle('is-loading', !!loading);
    getSelects(root).forEach(function (s) { s.disabled = !!loading; });
  }

  // data-msg-unavailable / -out-of-stock / -merged / -error
  function msg(root, name) {
    return root.getAttribute('data-msg-' + name) || '';
  }

  // Broadcast's own cart error box (updateErrorText + toggleErrorMessage). build() collapses it at
  // the end of every refresh, so it is re-opened on theme:product:added, the last event build() fires.
  function notify(message) {
    if (!message) return;
    var holder = document.querySelector('[data-cart-errors]');
    if (!holder) return;
    var show = function () {
      var text = holder.querySelector('[data-error-message]');
      if (text) text.innerText = message;
      holder.classList.add('is-expanded');
    };
    show();
    document.addEventListener('theme:product:added', show, { once: true });
    setTimeout(function () { document.removeEventListener('theme:product:added', show); }, 5000);
  }

  function sameProps(a, b) {
    var ka = Object.keys(a || {}).sort(), kb = Object.keys(b || {}).sort();
    return ka.length === kb.length && ka.every(function (k, i) { return k === kb[i] && a[k] === b[k]; });
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) {
          var err = new Error(data.description || data.message || '');
          err.payload = data;
          throw err;
        }
        return data;
      });
    });
  }

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  function handleChange(root) {
    if (root.classList.contains('is-loading')) return;

    var variants     = parseVariants(root);
    var lineKey      = root.dataset.lineKey;
    var currentId    = parseInt(root.dataset.currentVariantId, 10);
    var values       = getSelectedValues(root);
    var target       = findVariant(variants, values);

    if (!target) {
      notify(msg(root, 'unavailable'));
      revertToCurrentVariant(root, variants);
      refreshCart();
      return;
    }
    if (target.id === currentId) return;
    if (!isAvailable(target)) {
      notify(msg(root, 'out-of-stock'));
      revertToCurrentVariant(root, variants);
      refreshCart();
      return;
    }

    setLoading(root, true);

    fetchCart().then(function (cart) {
      var items       = (cart && cart.items) || [];
      var currentItem = items.find(function (it) { return it.key === lineKey; });
      // a linha já não existe (outro separador/pedido mudou o carrinho): só redesenhar
      if (!lineKey || !currentItem) return;

      var existingItem = items.find(function (it) { return it.variant_id === target.id && it.key !== lineKey; });
      var qty          = currentItem.quantity || 1;
      var properties   = currentItem.properties || {};

      if (existingItem) {
        // MERGE: sum quantities into existing line, remove current line (updates by line key)
        var updates = {};
        updates[existingItem.key] = (existingItem.quantity || 0) + qty;
        updates[lineKey] = 0;
        return postJson('/cart/update.js', { updates: updates }).then(function () {
          notify(msg(root, 'merged'));
        });
      }

      // SWAP: /cart/change.js cannot change the variant ID. Add the new variant FIRST, then
      // remove the old line — a failed add (stock) leaves the cart untouched. The old line is
      // re-read after the add: by key, else by old variant + same properties.
      return postJson('/cart/add.js', {
        id: target.id,
        quantity: qty,
        properties: properties
      }).then(fetchCart).then(function (after) {
        var list = (after && after.items) || [];
        var old = list.find(function (it) { return it.key === lineKey; }) ||
          list.find(function (it) { return it.variant_id === currentId && sameProps(it.properties, properties); });
        if (!old) return;
        return postJson('/cart/change.js', { id: old.key, quantity: 0 });
      });
    }).then(function () {
      refreshCart();
    }).catch(function (err) {
      // texto da Shopify (já na língua da loja) só quando veio do carrinho; o resto é o genérico
      notify((err.payload && err.message) || msg(root, 'error'));
      if (!err.payload) console.warn('rt-cart-variant-switcher:', err);
      revertToCurrentVariant(root, variants);
      setLoading(root, false);
      refreshCart();
    });
  }

  function init(root) {
    if (root.dataset[INIT_FLAG] === '1') return;
    root.dataset[INIT_FLAG] = '1';
    var variants = parseVariants(root);
    updateDisabledOptions(root, variants);
    root.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches(SELECTOR_SELECT)) {
        updateDisabledOptions(root, variants);
        handleChange(root);
      }
    });
  }

  function initAll() {
    document.querySelectorAll(SELECTOR_ROOT).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init after Broadcast re-renders the cart drawer (new DOM nodes need wiring)
  ['theme:cart:refresh', 'theme:cart:change', 'theme:cart:add', 'theme:cart:update'].forEach(function (evt) {
    document.addEventListener(evt, function () { setTimeout(initAll, 50); });
  });
})();
